import { useState, useEffect } from 'react';
import {
    Button,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Step,
    StepLabel,
    Stepper,
    useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import posthog from 'posthog-js';
import { enqueueSnackbar } from 'notistack';
import confetti from 'canvas-confetti';
import { NetProfileAuthsDocument } from '@/graphql/generated/net-profile-auths.generated';
import { GetApiClientDocument } from '@/graphql/generated/getApiClient.generated';
import { CreateApiClientDocument } from '@/graphql/generated/createApiClient.generated';
import { UpdateUserInfoDocument } from '@/graphql/generated/update-user-info.generated';
import { UpdateWorkspaceInfoDocument } from '@/graphql/generated/update-workspace-info.generated';
import { TeamPermission } from '@/graphql/generated/types.generated';
import { useSession } from '@/providers/session-provider';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { StepConnectProfile } from './StepConnectProfile';
import { StepApiCredentials } from './StepApiCredentials';

interface DevOnboardingDialogProps {
    open: boolean;
    onComplete: () => void;
}

export const DevOnboardingDialog = ({ open, onComplete }: DevOnboardingDialogProps) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { me, workspace } = useSession();
    const { teamPermissions } = useTeamPermissions();
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        if (open) {
            posthog.capture('onboarding.dev.started');
        }
    }, [open]);
    const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
    const [hasTriggeredKeyGeneration, setHasTriggeredKeyGeneration] = useState(false);

    const {
        data: profilesData,
        loading: loadingProfiles,
        refetch: refetchProfiles,
    } = useQuery(NetProfileAuthsDocument, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            cache: false,
        },
    });

    const { data: apiClientData, loading: loadingApiClient } = useQuery(GetApiClientDocument);

    const [updatePersonalInfo] = useMutation(UpdateUserInfoDocument, {
        onError: ({ message }) => enqueueSnackbar(message),
    });
    const [updateWorkspaceInfo] = useMutation(UpdateWorkspaceInfoDocument, {
        onError: ({ message }) => enqueueSnackbar(message),
    });

    const [createApiClient, { loading: creatingApiClient }] = useMutation(CreateApiClientDocument, {
        refetchQueries: [GetApiClientDocument],
        onCompleted: (data) => {
            const key = data?.createApiClient?.primaryKey || data?.createApiClient?.secondaryKey;
            if (key) {
                setGeneratedApiKey(key);
            }
        },
    });

    // Auto-generate API key when reaching step 1 if not already generated
    useEffect(() => {
        const existingClient = apiClientData?.getApiClient;
        const hasExistingKey = existingClient?.primaryKey || existingClient?.secondaryKey;

        if (
            activeStep === 1 &&
            !hasExistingKey &&
            !loadingApiClient &&
            !creatingApiClient &&
            !hasTriggeredKeyGeneration
        ) {
            setHasTriggeredKeyGeneration(true);
            createApiClient();
        }
    }, [
        activeStep,
        apiClientData,
        loadingApiClient,
        creatingApiClient,
        hasTriggeredKeyGeneration,
        createApiClient,
    ]);

    const hasProfiles = profilesData?.netProfileAuths?.total && profilesData.netProfileAuths.total > 0;

    const handleNext = () => {
        if (activeStep === 0 && !hasProfiles) {
            enqueueSnackbar(t('onboarding.dev.connect-profile-first'), { variant: 'warning' });
            return;
        }
        posthog.capture('onboarding.dev.step.next', { step: activeStep });
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleComplete = async () => {
        const emailName = me?.email?.split('@')[0] || '';

        await updatePersonalInfo({
            variables: { name: emailName },
        });

        if (teamPermissions.includes(TeamPermission.Owner) && workspace) {
            await updateWorkspaceInfo({
                variables: { name: emailName },
            });
        }

        posthog.capture('onboarding.dev.completed');
        confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
        });
        onComplete();
    };

    // Use generated key if available, otherwise check if there's an existing one
    const apiKey =
        generatedApiKey ||
        (apiClientData?.getApiClient?.primaryKey ? '••••••••••••••••••••••••••••••••' : null);
    const appId = apiClientData?.getApiClient?.id || 'your-app-id';

    // Map connected profiles for the code example
    const profiles = (profilesData?.netProfileAuths?.items ?? []).map((p) => ({
        id: p.id,
        name: p.netProfile.name ?? p.netProfile.username ?? 'Profile',
        net: p.netProfile.net.name,
    }));

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            disableEscapeKeyDown
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                },
                paper: {
                    sx: {
                        borderRadius: isMobile ? 0 : 4,
                    },
                },
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Stack gap={2} px={{ xs: 2, sm: 3 }} py={2}>
                    <CardHeader
                        title={t('onboarding.dev.title')}
                        subheader={t('onboarding.dev.subtitle')}
                        slotProps={{
                            title: { variant: 'h4', color: 'textPrimary' },
                            subheader: { variant: 'body1', color: 'textSecondary' },
                        }}
                        sx={{ p: 0 }}
                    />
                    <Stepper activeStep={activeStep} alternativeLabel connector={null} sx={{ gap: 1 }}>
                        {[0, 1].map((step) => (
                            <Step key={step} sx={{ p: 0 }}>
                                <StepLabel
                                    sx={{
                                        p: 0,
                                        '& > .MuiStepLabel-iconContainer': {
                                            borderRadius: 2,
                                            width: '100%',
                                            height: '8px',
                                            bgcolor: 'lightGrey',
                                            '&.Mui-active, &.Mui-completed': {
                                                bgcolor: 'primary.light',
                                            },
                                            '& > svg': { display: 'none' },
                                        },
                                    }}
                                />
                            </Step>
                        ))}
                    </Stepper>
                </Stack>
                <Divider />
            </DialogTitle>
            <DialogContent>
                {activeStep === 0 && (
                    <StepConnectProfile
                        profilesData={profilesData}
                        loadingProfiles={loadingProfiles}
                        refetchProfiles={refetchProfiles}
                    />
                )}

                {activeStep === 1 && (
                    <StepApiCredentials
                        loading={loadingApiClient || creatingApiClient}
                        appId={appId}
                        apiKey={apiKey}
                        isGeneratedKey={!!generatedApiKey}
                        profiles={profiles}
                    />
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" width="100%">
                    <Button onClick={handleBack} disabled={activeStep === 0}>
                        {t('str.action.escape.back')}
                    </Button>
                    <Stack direction="row" gap={1}>
                        {activeStep === 1 && apiKey ? (
                            <Button variant="contained" onClick={handleComplete}>
                                {t('onboarding.dev.finish')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={activeStep === 0 && !hasProfiles}
                            >
                                {t('str.action.progress.next')}
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
